import { Network } from "vis-network/standalone";
import { DataSet } from "vis-network/standalone";
import { Node } from "../types";
import {
  BadgeType,
  getBadgeColor,
  getBadgeDimensions,
  getNodeBadges,
  layoutBadges,
  LEASED_CARS_ICON,
} from "./badgeUtils";

// Cache for badge icons
const iconCache: {[key: string]: HTMLCanvasElement} = {};

export const createBadgeIcon = (text: string | number, type: BadgeType) => {
  const cacheKey = `${type}_${text}`;
  if (iconCache[cacheKey]) return iconCache[cacheKey];

  const badge = { type, value: text };
  const { width: displayWidth, height: displayHeight } = getBadgeDimensions(badge);
  const canvas = document.createElement('canvas');
  const scaleFactor = Math.max(window.devicePixelRatio || 2, 3); // Use higher scale factor for maximum quality
  const canvasWidth = displayWidth * scaleFactor;
  const canvasHeight = displayHeight * scaleFactor;

  // Set canvas internal size (high resolution)
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // Set canvas display size (what users see)
  canvas.style.width = displayWidth + 'px';
  canvas.style.height = displayHeight + 'px';

  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Scale the drawing context to match the high resolution
    ctx.scale(scaleFactor, scaleFactor);

    // Draw circular badges and wider pills for vehicle and tenant-case counts.
    ctx.beginPath();
    if (type === 'leasedCars' || type === 'tenantCase') {
      const radius = displayHeight / 2 - 1;
      ctx.moveTo(radius + 1, 1);
      ctx.lineTo(displayWidth - radius - 1, 1);
      ctx.arcTo(displayWidth - 1, 1, displayWidth - 1, radius + 1, radius);
      ctx.lineTo(displayWidth - 1, displayHeight - radius - 1);
      ctx.arcTo(displayWidth - 1, displayHeight - 1, displayWidth - radius - 1, displayHeight - 1, radius);
      ctx.lineTo(radius + 1, displayHeight - 1);
      ctx.arcTo(1, displayHeight - 1, 1, displayHeight - radius - 1, radius);
      ctx.lineTo(1, radius + 1);
      ctx.arcTo(1, 1, radius + 1, 1, radius);
    } else {
      ctx.arc(displayWidth / 2, displayHeight / 2, displayHeight / 2 - 1, 0, 2 * Math.PI);
    }

    ctx.fillStyle = getBadgeColor(type);
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Enable maximum quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';

    if (type === 'employmentSupport') {
      ctx.font = '900 12px "Font Awesome 6 Free", FontAwesome';
      ctx.fillText(text.toString(), displayWidth / 2, displayHeight / 2 + 0.5);
    } else if (type === 'leasedCars') {
      ctx.font = `900 10px "Font Awesome 6 Free", "Font Awesome 5 Free", FontAwesome`;
      ctx.fillText(LEASED_CARS_ICON, 11, displayHeight / 2 + 0.5);
      ctx.font = 'bold 11px Arial, sans-serif';
      ctx.fillText(text.toString(), 12 + (displayWidth - 12) / 2, displayHeight / 2 + 1);
    } else if (type === 'tenantCase') {
      // Draw the document directly so it stays centred and does not depend on
      // Font Awesome font metrics in the vis-network canvas.
      ctx.beginPath();
      ctx.moveTo(6.5, 5.5);
      ctx.lineTo(11.5, 5.5);
      ctx.lineTo(15.5, 9.5);
      ctx.lineTo(15.5, 18.5);
      ctx.lineTo(6.5, 18.5);
      ctx.closePath();
      ctx.moveTo(11.5, 5.5);
      ctx.lineTo(11.5, 9.5);
      ctx.lineTo(15.5, 9.5);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px Arial, sans-serif';
      ctx.fillText(text.toString(), 17 + (displayWidth - 17) / 2, displayHeight / 2 + 1);
    } else {
      ctx.font = `bold ${Math.floor(displayHeight * 0.55)}px Arial, sans-serif`;
      ctx.fillText(text.toString(), displayWidth / 2, displayHeight / 2 + 1);
    }
  }

  iconCache[cacheKey] = canvas;
  return canvas;
};

// Function to draw overlay icons on nodes
export const drawOverlayIcons = (
  ctx: CanvasRenderingContext2D,
  network: Network,
  graphNodes: DataSet<Node>,
  expandedNodes: Set<string>,
  sourceNodeId?: string,
  showCompanyComments: boolean = true,
  showIvoLicenses: boolean = false
) => {
  if (!network || !graphNodes) return;
  
  const iconSize = 24; // Match the display size
  
  // Get all nodes that should have overlay icons, excluding hidden nodes
  const allNodes = graphNodes.get();
  const nodesWithIcons = allNodes.filter((node: Node) => {
    // Skip hidden nodes - this fixes the issue with icons showing when nodes are filtered/hidden
    if ((node as any).hidden === true) return false;
    
    return getNodeBadges(node, {
      isExpanded: expandedNodes.has(node.id),
      isSource: Boolean(sourceNodeId && node.id === sourceNodeId),
      showCompanyComments,
      showIvoLicenses,
    }).length > 0;
  });
  
  nodesWithIcons.forEach((node: Node) => {
    // Get node center position
    const nodePositions = network.getPositions([node.id]);
    const nodeCenter = nodePositions[node.id];
    
    if (!nodeCenter || isNaN(nodeCenter.x) || isNaN(nodeCenter.y)) return;
    
    const isSource = Boolean(sourceNodeId && node.id === sourceNodeId);
    const badgesToRender = getNodeBadges(node, {
      isExpanded: expandedNodes.has(node.id),
      isSource,
      showCompanyComments,
      showIvoLicenses,
    });

    const startOffsetX = 10;
    const startOffsetY = -30;

    layoutBadges(badgesToRender).forEach((badge) => {
      const badgeX = nodeCenter.x + startOffsetX + badge.x;
      const badgeY = nodeCenter.y + startOffsetY + badge.y;
      
      const icon = createBadgeIcon(badge.value, badge.type);
      if (!icon) return;

      // Special rendering for 'bad' type (pulsing effect) and 'warning' type (watchlist)
      if (badge.type === 'bad' || badge.type === 'warning') {
        ctx.save();
        ctx.globalAlpha = (node as any).opacity ?? 1;
        
        // Calculate dynamic size: Make watchlist badge larger for source node
        // Base size is iconSize (24). 
        // If it is the source node AND it is the watchlist badge (warning), scale it up.
        let currentIconSize = iconSize;
        if (isSource && badge.type === 'warning') {
            currentIconSize = iconSize * 1.5; // 50% larger
        }

        const centerX = badgeX + iconSize / 2; // Center based on original grid position
        const centerY = badgeY + iconSize / 2;
        
        // Add pulsing glow effect
        const time = Date.now();
        const pulse = (Math.sin(time / 400) + 1) / 2; // 0 to 1 oscillating value (slower)
        
        // Use a radial gradient for a perfectly smooth fade without sharp edges
        const innerRadius = (currentIconSize / 2) - 2;
        const outerRadius = (currentIconSize / 2) + 12 + (pulse * 8); // Pulse the size more significantly
        
        const gradient = ctx.createRadialGradient(centerX, centerY, innerRadius, centerX, centerY, outerRadius);
        
        // Adjust opacity based on pulse
        const opacity = 0.6 + (pulse * 0.2);
        
        if (badge.type === 'bad') {
           gradient.addColorStop(0, `rgba(220, 53, 69, ${opacity})`);
        } else {
           // Warning / Watchlist - Orange/Yellow
           gradient.addColorStop(0, `rgba(243, 156, 18, ${opacity})`);
        }
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // Fade to transparent
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.globalAlpha = (node as any).opacity ?? 1;
        // Center the icon (if size changed)
        const drawX = centerX - currentIconSize / 2;
        const drawY = centerY - currentIconSize / 2;
        ctx.drawImage(icon, drawX, drawY, currentIconSize, currentIconSize);
        ctx.restore();
      } else {
        // Standard rendering for other types
        ctx.save();
        ctx.globalAlpha = (node as any).opacity ?? 1;
        ctx.drawImage(icon, badgeX, badgeY, badge.width, badge.height);
        ctx.restore();
      }
    });
  });
};
